import { TypographyV3 } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';

import Motion from '@/app/components/Motion';

import type {
  LandingPageIntegrationData,
  LandingPageIntegrationsLogoColor,
} from './types';

export interface IntegrationProps extends LandingPageIntegrationData {
  readonly className?: string | undefined;
  readonly logoColor: LandingPageIntegrationsLogoColor;
  readonly index: number;
  readonly shouldTransition: boolean;
  readonly inverted: boolean;
}

const Integration = ({
  className,
  description,
  Logo,
  logoClassName = '',
  logoColor,
  index,
  name,
  shouldTransition,
  inverted,
  title,
}: IntegrationProps): JSX.Element => {
  const t = useTranslations();

  return (
    <div
      className={cx(
        'w-full flex-initial',
        'md:w-[calc(50%-2rem)]',
        'lg:w-[calc(33.333%-2.667rem)]',
        'xl:w-[calc(33.333%-4rem)]',
        'xxl:w-[calc(33.333%-4.667rem)]',
        className,
      )}
      data-cy="landing-page-integration"
    >
      <Motion
        animate={{ opacity: shouldTransition ? 1 : 0 }}
        className="flex w-full flex-col items-center text-center"
        initial={{ opacity: 0 }}
        transition={{ delay: 0.05 * index, duration: 0.5, type: 'tween' }}
        viewport={{ once: true }}
      >
        {Logo && (
          <Logo
            aria-hidden="false"
            className={cx(
              'mb-4 h-10',
              {
                'fill-black': logoColor === 'black',
                'fill-current': logoColor === 'current',
              },
              logoClassName,
            )}
            color={logoColor}
            data-cy="landing-page-integration-logo"
            title={name}
          />
        )}
        {title && (
          <TypographyV3
            align="center"
            className="mb-2 font-normal"
            color={inverted ? 'light' : 'inherit'}
            variant="h3"
            font="serif"
          >
            {t(title)}
          </TypographyV3>
        )}
        {description && (
          <TypographyV3
            align="center"
            className="max-w-96 leading-snug"
            color={inverted ? 'light' : 'inherit'}
            data-cy="landing-page-integration-description"
          >
            {t(description)}
          </TypographyV3>
        )}
      </Motion>
    </div>
  );
};

export default Integration;
